/**
 * BusinessRegistration — apply-first onboarding for ShahoSuite
 * Wholesale Suite · Landlord Suite · Super Admin approves before demo access
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';

type SuiteId = 'wholesale' | 'landlord';

interface SuiteConfig {
  id: SuiteId;
  title: string;
  subtitle: string;
  audience: string;
  accent: string;
  grad: string;
  highlights: { icon: string; title: string; desc: string }[];
}

const SUITES: SuiteConfig[] = [
  {
    id: 'wholesale',
    title: 'Wholesale Suite',
    subtitle: 'Trade · Grocery · Cash & Carry',
    audience: 'Wholesale distributors, grocery suppliers, cash & carry, B2B trade and product-led retailers.',
    accent: '#2563eb',
    grad: 'linear-gradient(135deg,#2563eb,#7c3aed)',
    highlights: [
      { icon: '👤', title: 'Customers & CRM', desc: 'Manage trade accounts, contacts and customer history.' },
      { icon: '📦', title: 'Stock & suppliers', desc: 'Products, categories, purchase orders and stock movements.' },
      { icon: '🛒', title: 'Orders & invoicing', desc: 'Sales orders, invoices and credit notes in one flow.' },
      { icon: '💰', title: 'Finance & analytics', desc: 'Accounting overview and business performance insights.' },
      { icon: '🛍️', title: 'B2B online shop', desc: 'Wholesale portal with trade pricing and bulk orders.' },
      { icon: '📅', title: 'Booking & messaging', desc: 'Appointments, SMS/email and support built in.' },
      { icon: '🧑‍💼', title: 'Team & audit', desc: 'Role-based staff access with full audit trail.' },
    ],
  },
  {
    id: 'landlord',
    title: 'Landlord Suite',
    subtitle: 'Property · Lettings · Compliance',
    audience: 'UK landlords, letting agents and property managers with portfolios to run properly.',
    accent: '#0d9488',
    grad: 'linear-gradient(135deg,#0d9488,#0891b2)',
    highlights: [
      { icon: '🏘️', title: 'Portfolio & leases', desc: 'Properties, tenants, leases and sales pipeline.' },
      { icon: '💷', title: 'Rent & accounting', desc: 'Rent tracker, arrears and financial overview.' },
      { icon: '📁', title: 'Compliance & certs', desc: 'Gas, EPC, EICR and document management.' },
      { icon: '🛠️', title: 'Maintenance', desc: 'Jobs, contractors and property upkeep.' },
      { icon: '📅', title: 'Viewings & inventory', desc: 'Inspections, check-ins and property inventory.' },
      { icon: '📨', title: 'Team & messaging', desc: 'Staff, tenant messages and support.' },
      { icon: '📋', title: 'Settings & audit', desc: 'Business settings with full activity history.' },
    ],
  },
];

const REFERRAL_OPTIONS = [
  'LinkedIn',
  'Facebook',
  'Google search',
  'Friend or colleague',
  'College or university',
  'Professional contact',
  'Networking event',
  'Other',
];

const REFERRAL_DETAIL_SOURCES = new Set([
  'Friend or colleague',
  'College or university',
  'Professional contact',
  'Networking event',
  'Other',
]);

const referralDetailHint = (source: string): { label: string; placeholder: string } => {
  switch (source) {
    case 'Friend or colleague':
      return { label: 'Who referred you?', placeholder: 'e.g. John Smith at ABC Ltd' };
    case 'College or university':
      return { label: 'Which college or course?', placeholder: 'e.g. COPC, business admin' };
    case 'Professional contact':
      return { label: 'Which contact or organisation?', placeholder: 'e.g. accountant, trade association' };
    case 'Networking event':
      return { label: 'Which event?', placeholder: 'e.g. Birmingham trade fair, June 2026' };
    default:
      return { label: 'Tell us a bit more', placeholder: 'Optional — helps us understand' };
  }
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Outfit:wght@700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.reg { min-height: 100vh; background: #fff; color: #0f172a; font-family: 'Inter', system-ui, sans-serif; padding-bottom: 64px; }
.nav { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0; background: #fff; z-index: 10; }
.logo { font-family: 'Outfit', sans-serif; font-weight: 900; font-size: 18px; }
.logo span { background: linear-gradient(135deg,#2563eb,#7c3aed); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.hero { text-align: center; padding: 48px 20px 32px; max-width: 720px; margin: 0 auto; }
.hero h1 { font-family: 'Outfit', sans-serif; font-size: clamp(28px,4vw,44px); font-weight: 900; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 12px; }
.hero p { color: #64748b; font-size: 16px; line-height: 1.6; }
.badge { display: inline-flex; gap: 8px; align-items: center; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 999px; padding: 8px 16px; font-size: 12px; font-weight: 700; color: #64748b; margin-bottom: 20px; }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; max-width: 1100px; margin: 0 auto; padding: 0 20px; }
.card { border: 1.5px solid #e2e8f0; border-radius: 20px; padding: 28px; background: #fff; display: flex; flex-direction: column; transition: box-shadow .2s, border-color .2s; }
.card:hover { border-color: #cbd5e1; box-shadow: 0 12px 40px rgba(15,23,42,.08); }
.card-h { margin-bottom: 16px; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 22px; font-weight: 900; margin-bottom: 4px; }
.card-sub { font-size: 13px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 10px; }
.card-aud { font-size: 14px; color: #475569; line-height: 1.55; margin-bottom: 20px; }
.hl { display: flex; gap: 12px; align-items: flex-start; padding: 10px 0; border-top: 1px solid #f1f5f9; }
.hl-icon { font-size: 18px; line-height: 1; margin-top: 2px; }
.hl-title { font-size: 13px; font-weight: 700; color: #0f172a; }
.hl-desc { font-size: 12px; color: #64748b; line-height: 1.45; margin-top: 2px; }
.cta { margin-top: auto; padding-top: 20px; }
.btn { width: 100%; padding: 14px 20px; border: none; border-radius: 12px; font-weight: 700; font-size: 15px; cursor: pointer; color: #fff; transition: transform .15s, box-shadow .15s; }
.btn:hover { transform: translateY(-1px); }
.form-wrap { max-width: 520px; margin: 0 auto; padding: 40px 20px; }
.form-head { margin-bottom: 28px; }
.form-head h2 { font-family: 'Outfit', sans-serif; font-size: 28px; font-weight: 900; margin-bottom: 8px; }
.form-head p { color: #64748b; font-size: 14px; line-height: 1.5; }
.suite-pill { display: inline-block; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 700; margin-bottom: 12px; }
.fg { margin-bottom: 18px; }
.fl { display: block; font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 6px; }
.fi, .fs, .fta { width: 100%; padding: 12px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 15px; font-family: inherit; background: #fff; }
.fi:focus, .fs:focus, .fta:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(59,130,246,.15); }
.fe { color: #dc2626; font-size: 12px; margin-top: 4px; display: block; }
.row { display: flex; gap: 12px; margin-top: 24px; }
.btn-ghost { flex: 1; padding: 14px; border: 1.5px solid #e2e8f0; border-radius: 12px; background: #fff; font-weight: 600; cursor: pointer; }
.terms { display: flex; gap: 10px; align-items: flex-start; margin: 20px 0; font-size: 13px; color: #475569; line-height: 1.5; }
.terms-box { width: 20px; height: 20px; border: 2px solid #cbd5e1; border-radius: 5px; flex-shrink: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; }
.terms-box.on { background: #2563eb; border-color: #2563eb; color: #fff; }
.err { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; padding: 12px 14px; border-radius: 10px; font-size: 14px; margin-bottom: 16px; }
.ok-wrap { max-width: 520px; margin: 0 auto; padding: 60px 20px; text-align: center; }
.ok-icon { font-size: 56px; margin-bottom: 20px; }
.ok-wrap h2 { font-family: 'Outfit', sans-serif; font-size: 32px; font-weight: 900; margin-bottom: 12px; }
.ok-wrap p { color: #475569; line-height: 1.65; margin-bottom: 12px; }
.ok-box { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 14px; padding: 20px; text-align: left; margin: 24px 0; font-size: 14px; color: #166534; line-height: 1.6; }
.legal { max-width: 1100px; margin: 32px auto 0; padding: 0 20px; text-align: center; font-size: 12px; color: #94a3b8; }
`;

const BusinessRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [suiteId, setSuiteId] = useState<SuiteId>('wholesale');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    businessName: '',
    companyNumber: '',
    phone: '',
    referralSource: '',
    referralDetail: '',
    applicationNote: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subErr, setSubErr] = useState('');
  const [refId, setRefId] = useState('');

  const suite = SUITES.find((s) => s.id === suiteId)!;
  const upd = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.businessName.trim()) e.businessName = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!terms) {
      setSubErr('Please accept the Terms and Privacy Policy.');
      return;
    }
    setSubErr('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/registration/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactName: form.fullName,
          businessName: form.businessName,
          email: form.email,
          phone: form.phone,
          companyNumber: form.companyNumber,
          suite: suiteId,
          referralSource: form.referralSource,
          referralDetail: form.referralDetail,
          applicationNote: form.applicationNote,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubErr(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setRefId(data.registrationId || '');
      setStep(2);
    } catch {
      setSubErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reg">
      <style>{CSS}</style>

      <nav className="nav">
        <div className="logo">Shaho<span>Suite</span></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a
            href="https://www.shahosuite.com"
            className="btn-ghost"
            style={{ flex: 'none', padding: '8px 16px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            Homepage
          </a>
          <button
            type="button"
            className="btn"
            style={{ flex: 'none', width: 'auto', padding: '8px 18px', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}
            onClick={() => navigate('/login')}
          >
            Login →
          </button>
        </div>
      </nav>

      {step === 0 && (
        <>
          <div className="hero">
            <div className="badge">🇬🇧 UK-built · Apply for access · Reviewed within 1 business day</div>
            <h1>CRM built for UK trade &amp; property</h1>
            <p>
              Choose your suite, submit a short application, and our team will review it.
              Approved businesses receive a guided demo trial — no instant self-signup, no empty accounts.
            </p>
          </div>

          <div className="cards">
            {SUITES.map((s) => (
              <div key={s.id} className="card">
                <div className="card-h">
                  <div className="card-title">{s.title}</div>
                  <div className="card-sub">{s.subtitle}</div>
                  <p className="card-aud">{s.audience}</p>
                </div>
                {s.highlights.map((h) => (
                  <div key={h.title} className="hl">
                    <span className="hl-icon">{h.icon}</span>
                    <div>
                      <div className="hl-title">{h.title}</div>
                      <div className="hl-desc">{h.desc}</div>
                    </div>
                  </div>
                ))}
                <div className="cta">
                  <button
                    type="button"
                    className="btn"
                    style={{ background: s.grad, boxShadow: `0 8px 24px ${s.accent}30` }}
                    onClick={() => { setSuiteId(s.id); setStep(1); }}
                  >
                    Apply for {s.title} →
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className="legal">
            © {new Date().getFullYear()} Shaho Solutions Limited · ShahoSuite is proprietary software.
            Feature summaries are illustrative; full access is granted only after approval.
          </p>
        </>
      )}

      {step === 1 && (
        <div className="form-wrap">
          <div className="form-head">
            <span className="suite-pill" style={{ background: `${suite.accent}15`, color: suite.accent }}>
              {suite.title}
            </span>
            <h2>Apply for access</h2>
            <p>Tell us about your business. We review every application personally before granting demo access.</p>
          </div>

          {[
            { k: 'fullName', l: 'Full name', p: 'Jane Smith', t: 'text' },
            { k: 'email', l: 'Work email', p: 'jane@yourcompany.co.uk', t: 'email' },
            { k: 'businessName', l: 'Company name', p: 'Acme Wholesale Ltd', t: 'text' },
            { k: 'companyNumber', l: 'Company number', p: '12345678 (optional)', t: 'text', opt: true },
            { k: 'phone', l: 'Phone', p: '07700 000000', t: 'tel' },
          ].map((f) => (
            <div key={f.k} className="fg">
              <label className="fl">{f.l}{f.opt ? '' : ' *'}</label>
              <input
                type={f.t}
                className="fi"
                value={(form as Record<string, string>)[f.k]}
                onChange={(e) => upd(f.k, e.target.value)}
                placeholder={f.p}
              />
              {errors[f.k] && <span className="fe">{errors[f.k]}</span>}
            </div>
          ))}

          <div className="fg">
            <label className="fl">How did you hear about ShahoSuite?</label>
            <select
              className="fs"
              value={form.referralSource}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  referralSource: v,
                  referralDetail: REFERRAL_DETAIL_SOURCES.has(v) ? f.referralDetail : '',
                }));
              }}
            >
              <option value="">Select…</option>
              {REFERRAL_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {REFERRAL_DETAIL_SOURCES.has(form.referralSource) && (() => {
            const hint = referralDetailHint(form.referralSource);
            return (
              <div className="fg">
                <label className="fl">{hint.label} <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></label>
                <input
                  type="text"
                  className="fi"
                  maxLength={120}
                  value={form.referralDetail}
                  onChange={(e) => upd('referralDetail', e.target.value)}
                  placeholder={hint.placeholder}
                />
              </div>
            );
          })()}

          <div className="fg">
            <label className="fl">What are you trying to fix? (optional)</label>
            <textarea
              className="fta"
              rows={3}
              value={form.applicationNote}
              onChange={(e) => upd('applicationNote', e.target.value)}
              placeholder="e.g. outgrown spreadsheets, need B2B ordering, manage 12 rental properties…"
            />
          </div>

          <div className="terms">
            <div className={`terms-box${terms ? ' on' : ''}`} onClick={() => setTerms((t) => !t)} role="checkbox" aria-checked={terms}>
              {terms ? '✓' : ''}
            </div>
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: suite.accent, fontWeight: 600 }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: suite.accent, fontWeight: 600 }}>Privacy Policy</a>.
            </span>
          </div>

          {subErr && <div className="err">⚠️ {subErr}</div>}

          <div className="row">
            <button type="button" className="btn-ghost" onClick={() => setStep(0)}>Back</button>
            <button
              type="button"
              className="btn"
              disabled={loading}
              style={{ flex: 2, background: suite.grad, opacity: loading ? 0.75 : 1 }}
              onClick={() => { if (validate()) handleSubmit(); }}
            >
              {loading ? 'Submitting…' : 'Submit application →'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="ok-wrap">
          <div className="ok-icon">✅</div>
          <h2>Application received</h2>
          <p>
            Thank you, <strong>{form.fullName}</strong>. We have your application for{' '}
            <strong>{form.businessName}</strong> ({suite.title}).
          </p>
          <div className="ok-box">
            <strong>What happens next</strong>
            <ul style={{ margin: '10px 0 0 18px' }}>
              <li>We review your application within <strong>1 business day</strong></li>
              <li>If approved, login details for a <strong>14-day demo</strong> are sent to <strong>{form.email}</strong></li>
              <li>No account is created until we approve your application</li>
            </ul>
            {refId && (
              <p style={{ marginTop: 12, fontSize: 12, color: '#15803d' }}>Reference: {refId.slice(0, 8).toUpperCase()}</p>
            )}
          </div>
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Questions? Email <a href="mailto:admin@shahosuite.com" style={{ color: '#2563eb' }}>admin@shahosuite.com</a>
          </p>
          <button
            type="button"
            className="btn"
            style={{ marginTop: 8, maxWidth: 320, background: suite.grad }}
            onClick={() => navigate('/login')}
          >
            Back to login
          </button>
        </div>
      )}
    </div>
  );
};

export default BusinessRegistration;
